import React, { useMemo } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import styled from "@emotion/styled";
import { useIntl } from "react-intl";

import Button from "components/Button";
import DatePicker from "components/DatePicker";
import SelectField from "components/SelectField";

import ParkkiTheme from "themes/ParkkiTheme";

const FiltersContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-left: ${ParkkiTheme.spacings.small};
  padding-top: ${ParkkiTheme.spacings.tiny};
  padding-bottom: ${ParkkiTheme.spacings.tiny};
  max-height: 50px;
`;

const FilterContainer = styled.div`
  flex: 1;
  margin-top: 7px;
  margin-right: ${ParkkiTheme.spacings.small};
  max-width: 40%;
`;

function FlowFilters({
  flowTypes,
  endDate,
  search,
  startDate,
  setFilter,
  hideFlowTypes,
}) {
  const intl = useIntl();

  const checkedFlowTypes = useMemo(() => {
    return flowTypes
      ?.filter((filter) => filter.selected)
      .map((filter) => filter.value);
  }, [flowTypes]);

  function shouldDisableStartDate(date) {
    const minStartDate = moment().subtract(startDate?.maxDaysAllowed, "days");

    return date.isAfter(moment(), "day") || date.isBefore(minStartDate, "day");
  }

  function shouldDisabledEndDate(date) {
    return date.isAfter(moment(), "day");
  }

  function handleFlowTypesChange(event) {
    const updatedTypes = event?.target?.value;
    if (!updatedTypes || updatedTypes?.length === 0) return null;

    return setFilter("flowTypes", updatedTypes);
  }

  return (
    <FiltersContainer>
      <FilterContainer>
        <DatePicker
          disabled={!startDate.value}
          value={startDate.value}
          label={startDate.label}
          onChange={startDate.onChange}
          shouldDisableDate={shouldDisableStartDate}
        />
      </FilterContainer>

      <FilterContainer>
        <DatePicker
          disabled={!startDate.value}
          value={endDate.value}
          label={endDate.label}
          onChange={endDate.onChange}
          shouldDisableDate={shouldDisabledEndDate}
        />
      </FilterContainer>

      {!hideFlowTypes && (
        <FilterContainer>
          <SelectField
            id="filters"
            checkbox
            disabled={!flowTypes || flowTypes?.length <= 1}
            label={intl.messages["flowScene.filters.selectLabel"]}
            values={flowTypes}
            checked={checkedFlowTypes}
            onChange={handleFlowTypesChange}
          />
        </FilterContainer>
      )}

      <FilterContainer>
        <Button
          fullWidth={false}
          label={search.label}
          disabled={search.disabled}
          onClick={search.onClick}
        />
      </FilterContainer>
    </FiltersContainer>
  );
}

FlowFilters.defaultProps = {
  hideFlowTypes: false,
};

FlowFilters.propTypes = {
  endDate: PropTypes.shape({
    value: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
  }).isRequired,
  startDate: PropTypes.shape({
    value: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
  }).isRequired,
  search: PropTypes.shape({
    disabled: PropTypes.bool,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
  }).isRequired,
  hideFlowTypes: PropTypes.bool,
};

export default FlowFilters;
